/**
 * Dialog
 * Classification: mui-overrides (themed surface + radius)
 * MUI base: https://mui.com/material-ui/react-dialog/
 *
 * The action row is positioned by the theme (`MuiDialogActions`): gutters that
 * match the title/content, breathing room at the bottom, and a token gap
 * between buttons — no per-usage `sx` needed. `DialogActions` here is a thin
 * wrapper exposing the common adjustments as props: `align` and `dense`.
 */
import MuiDialogActions from '@mui/material/DialogActions';
import type { DialogActionsProps as MuiDialogActionsProps } from '@mui/material/DialogActions';
import { spacing } from '@/tokens/spacing';

export { default as Dialog } from '@mui/material/Dialog';
export type { DialogProps } from '@mui/material/Dialog';
export { default as DialogTitle } from '@mui/material/DialogTitle';
export { default as DialogContent } from '@mui/material/DialogContent';
export { default as DialogContentText } from '@mui/material/DialogContentText';

/** Where the action buttons sit within the dialog's footer row. */
export type DialogActionsAlign = 'start' | 'center' | 'end' | 'stretch';

export interface DialogActionsProps extends MuiDialogActionsProps {
  /**
   * Button placement. Defaults to `end` (right-aligned); `stretch` shares the
   * full row width between the actions.
   */
  align?: DialogActionsAlign;
  /** Tighter gutters for compact confirmation dialogs. */
  dense?: boolean;
}

const justifyByAlign: Record<DialogActionsAlign, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'flex-end',
};

export function DialogActions({ align = 'end', dense = false, sx, ...rest }: DialogActionsProps) {
  return (
    <MuiDialogActions
      {...rest}
      sx={[
        {
          justifyContent: justifyByAlign[align],
          ...(align === 'stretch' ? { '& > *': { flexGrow: 1 } } : null),
          ...(dense
            ? { padding: `${spacing.sm}px ${spacing.md}px ${spacing.md}px` }
            : null),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}
